import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import translations, { SUPPORTED_LOCALES } from "./translations";

const I18nContext = createContext(null);

const VALID = SUPPORTED_LOCALES.map((l) => l.code);

function detectBrowserLocale() {
  if (typeof navigator === "undefined") return "en";
  const langs = navigator.languages || [navigator.language || "en"];
  for (const l of langs) {
    const low = l.toLowerCase();
    if (low.startsWith("pt")) return "pt-br";
    if (low.startsWith("es")) return "es";
    if (low.startsWith("zh")) return "zh";
    if (low.startsWith("en")) return "en";
  }
  return "en";
}

export function I18nProvider({ children }) {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const initialLocale = useMemo(() => {
    const fromUrl = (params.lang || "").toLowerCase();
    if (VALID.includes(fromUrl)) return fromUrl;
    try {
      const stored = localStorage.getItem("gaelon-locale");
      if (stored && VALID.includes(stored)) return stored;
    } catch (_e) { /* ignore */ }
    return detectBrowserLocale();
  }, [params.lang]);

  const [locale, setLocaleState] = useState(initialLocale);

  // Sync URL → state
  useEffect(() => {
    const fromUrl = (params.lang || "").toLowerCase();
    if (VALID.includes(fromUrl) && fromUrl !== locale) {
      setLocaleState(fromUrl);
    }
  }, [params.lang, locale]);

  // Sync HTML lang + persist
  useEffect(() => {
    const t = translations[locale] || translations.en;
    document.documentElement.lang = t.htmlLang;
    try { localStorage.setItem("gaelon-locale", locale); } catch (_e) { /* ignore */ }
  }, [locale]);

  const setLocale = useCallback(
    (next) => {
      if (!VALID.includes(next)) return;
      setLocaleState(next);
      // Update URL prefix /:lang while preserving hash & rest
      const path = location.pathname;
      const parts = path.split("/").filter(Boolean);
      const first = (parts[0] || "").toLowerCase();
      let newPath;
      if (VALID.includes(first)) {
        parts[0] = next;
        newPath = "/" + parts.join("/");
      } else {
        newPath = "/" + next + (path === "/" ? "" : path);
      }
      navigate(newPath + location.hash, { replace: false });
    },
    [location.pathname, location.hash, navigate]
  );

  const t = useMemo(() => translations[locale] || translations.en, [locale]);

  const value = useMemo(() => ({ locale, setLocale, t, locales: SUPPORTED_LOCALES }), [locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
}
